import { useEffect, useState } from "react";
import { Share, PlusSquare, X, Download } from "lucide-react";
import { usePersistedState } from "../lib/useSort";
import { useIsMobile } from "../lib/useMobile";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

/** Phone-only nudge to install the dashboard on the home screen (spiral icon). */
export default function InstallHint() {
  const mobile = useIsMobile();
  const [dismissedAt, setDismissedAt] = usePersistedState("bm-install-hint:dismissed", 0);
  const [promptEvt, setPromptEvt] = useState(null);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPromptEvt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const snoozed = dismissedAt && Date.now() - dismissedAt < 30 * 24 * 3600 * 1000;
  if (!mobile || isStandalone() || snoozed) return null;

  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const dismiss = () => setDismissedAt(Date.now());

  const install = async () => {
    if (!promptEvt) return;
    promptEvt.prompt();
    await promptEvt.userChoice.catch(() => {});
    setPromptEvt(null);
    dismiss();
  };

  return (
    <div
      data-testid="install-hint"
      className="md:hidden fixed inset-x-3 z-30 bg-[#0a0a0a] text-[#f5f2eb] border-2 border-[#0a0a0a] shadow-[6px_6px_0_0_#c72c2c] p-3 flex items-center gap-3"
      style={{ bottom: "calc(64px + env(safe-area-inset-bottom) + 8px)" }}
    >
      <img src="/favicon-192.png" alt="" className="w-11 h-11 shrink-0" draggable={false} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium leading-tight">Add to your home screen</div>
        <div className="bm-eyebrow opacity-75 mt-1 leading-snug" data-testid="install-hint-steps">
          {promptEvt ? (
            "Opens full-screen like an app"
          ) : isIOS ? (
            <>Tap <Share size={11} className="inline -mt-0.5" /> Share, then <PlusSquare size={11} className="inline -mt-0.5" /> Add to Home Screen</>
          ) : (
            <>Browser menu (⋮) → <span className="text-[#f5f2eb]">Install app</span> or <span className="text-[#f5f2eb]">Add to Home screen</span></>
          )}
        </div>
      </div>
      {promptEvt && (
        <button onClick={install} data-testid="install-hint-install" className="shrink-0 min-h-[44px] px-3 bm-eyebrow bg-[#c72c2c] text-[#f5f2eb] flex items-center gap-1">
          <Download size={12} /> Install
        </button>
      )}
      <button onClick={dismiss} data-testid="install-hint-dismiss" aria-label="Dismiss" className="shrink-0 w-11 h-11 flex items-center justify-center hover:bg-white/10">
        <X size={16} />
      </button>
    </div>
  );
}
